import type { PixelEvent } from '@/events';

import type { EventSender, SendOptions } from './types';

export type TimedSenderConfig = {
  readonly timeoutMs: number;
};

export class TimedSender implements EventSender {
  private readonly inner: EventSender;

  private readonly timeoutMs: number;

  private abortController: AbortController | null = null;

  constructor(inner: EventSender, config: TimedSenderConfig) {
    this.inner = inner;
    this.timeoutMs = config.timeoutMs;
  }

  async send(
    events: PixelEvent[],
    options?: SendOptions
  ): Promise<{ ack: boolean; eventCount: number }> {
    this.abortController?.abort();
    this.abortController = new AbortController();

    const controller = this.abortController;
    const signals = [controller.signal, options?.signal].filter(
      Boolean
    ) as AbortSignal[];

    const combined = combineAbortSignals(signals);

    const timeoutId = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      return await this.inner.send(events, {
        ...options,
        signal: combined,
      });
    } finally {
      clearTimeout(timeoutId);
      if (this.abortController === controller) {
        this.abortController = null;
      }
    }
  }

  abortPending(): void {
    this.abortController?.abort();
    this.abortController = null;
  }
}

function combineAbortSignals(signals: AbortSignal[]): AbortSignal | undefined {
  if (signals.length === 0) return undefined;
  if (signals.length === 1) return signals[0];

  const controller = new AbortController();

  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort();
      return controller.signal;
    }
    signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  return controller.signal;
}
